import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import queryString from 'query-string';
import { EQUIPMENT_CODES_TYPEAHEAD_OPTIONS_SEARCH } from '../../../../services/data-store.gql';
import { useFederalStandards } from '../../fvs-provider';
import { useFvsRequirements } from '../requirements-provider';
import { getFilterStructure } from './requirement-filters/requirement-filter-structure';

const RequirementsFilter = ({ setFilters }) => {
  const {
    applicableFvsYears,
    selectedOptions,
    selectedYear,
    standardItemsAsOptions,
    standardItem,
  } = useFederalStandards();

  const navigate = useNavigate();
  const location = useLocation();

  const { FilterPanel: RequirementsFilterPanel } = FilterPanel;

  const { lookupCodes } = useFvsRequirements();

  // Default filter values derived from provider state (synced from URL by parent)
  const defaultFilters = React.useMemo(
    () => ({
      year: selectedYear?.toString() || '',
      standardItem: selectedOptions?.standardItemNumber || '',
    }),
    [selectedYear, selectedOptions?.standardItemNumber],
  );

  const [typeaheadData, setTypeaheadData] = useState(null);
  const [searchKey, setSearchKey] = useState(null);

  const [getTypeAheadOptions] = useLazyQuery(
    EQUIPMENT_CODES_TYPEAHEAD_OPTIONS_SEARCH,
    {
      fetchPolicy: 'network-only',
      onCompleted: ({ getEquipmentCodesTypeAheadOptions }) => {
        if (getEquipmentCodesTypeAheadOptions) {
          const { options } = getEquipmentCodesTypeAheadOptions;
          // return unique options only.
          const uniqueOptions = Array.from(new Set(options));
          setTypeaheadData({
            field: searchKey,
            values: uniqueOptions,
          });
        }
      },
    },
  );

  const handleFiltersChange = (updatedFilters) => {
    const filters = {
      operator: 'AND',
      value: [],
    };

    // Map filters to key-value object
    const filterByKeys = updatedFilters?.reduce(
      (filterObj, currentFilterObj) => {
        return { ...filterObj, [currentFilterObj?.key]: currentFilterObj };
      },
      {},
    );

    // Extract state from filters
    const state = {
      year: Number.parseInt(filterByKeys?.year?.value, 10),
      standardItem: filterByKeys?.standardItem?.value,
    };

    // EARLY RETURN: Only proceed if standardItem is present
    const isYearValid = !Number.isNaN(state.year) && state.year > 0;
    const isStandardItemValid = state.standardItem?.toString()?.trim();

    if (!isYearValid || !isStandardItemValid) {
      return;
    }

    // Update URL instead of context — the Requirements index.jsx effects
    // will sync URL params to context, keeping a single source of truth.
    const yearChanged = Number(selectedYear) !== Number(state.year);
    const standardItemChanged =
      selectedOptions?.standardItemNumber !== state.standardItem;

    if (yearChanged || standardItemChanged) {
      const params = queryString.parse(location.search);
      params['filter-year'] = state.year;
      params['filter-standardItem'] = state.standardItem;
      navigate(`${location.pathname}?${queryString.stringify(params)}`, {
        replace: true,
      });
    }

    if (filterByKeys.associationType) {
      filters.value.push({
        key: 'associationTypeCode',
        operator: 'EQ',
        value: filterByKeys.associationType?.value,
      });
    }

    if (filterByKeys.category) {
      filters.value.push({
        key: '$equipmentCode.category$',
        operator: 'EQ',
        value: filterByKeys.category?.value,
      });
    }

    if (filterByKeys.title) {
      filters.value.push({
        key: '$equipmentCode.title$',
        operator: 'EQ',
        value: filterByKeys.title?.value,
      });
    }

    if (filterByKeys.code) {
      filters.value.push({
        key: '$equipmentCode.code$',
        operator: 'EQ',
        value: filterByKeys.code?.value,
      });
    }

    // Add year filter
    const yearValue = Number.parseInt(state.year, 10);
    if (!Number.isNaN(yearValue) && yearValue > 0) {
      filters.value.push({
        key: '$standardItem.year$',
        operator: 'EQ',
        value: yearValue,
      });
    }

    // Add standardItem filter
    if (state.standardItem?.toString()?.trim()) {
      filters.value.push({
        key: '$standardItem.standard_item_number$',
        operator: 'EQ',
        value: state.standardItem,
      });
    }

    filters.value.push(
      {
        key: '$equipmentCode.is_active$',
        operator: 'EQ',
        value: 1,
      },
      {
        key: '$equipmentCode.deleted_at$',
        operator: 'EQ',
        value: null,
      },
    );

    setTypeaheadData(null);
    setFilters(filters);
  };

  const handleFetchTypeaheads = ({ variables: queryVariables }) => {
    const selectedKey = queryVariables?.field;
    setSearchKey(selectedKey);
    const filterObj = queryVariables?.filters[0]?.conditions?.find(
      (filter) => filter.key === selectedKey,
    );
    const conditions = [
      { operator: '$exact', key: 'year', value: `${selectedYear}` },
    ];
    if (standardItem?.program)
      conditions.push({
        operator: '$exact',
        key: 'program',
        value: standardItem.program,
      });
    getTypeAheadOptions({
      variables: {
        key: selectedKey,
        search: filterObj.value,
        filters: [{ operator: '$and', conditions }],
      },
    });
  };

  return (
    <RequirementsFilterPanel
      key={`${defaultFilters.year}-${defaultFilters.standardItem}`}
      clearButtonLabel="Reset All"
      filterStructure={getFilterStructure(
        applicableFvsYears,
        defaultFilters,
        [{ label: 'Select', value: '' }, ...standardItemsAsOptions],
        lookupCodes,
      )}
      model="standardItem"
      order={[['standardItemNumber', 'ASC']]}
      setQueryFiltersState={handleFiltersChange}
      fetchTypeaheads={handleFetchTypeaheads}
      typeaheadData={typeaheadData}
      showClearIcon
      retainFilters
    />
  );
};

RequirementsFilter.propTypes = {
  setFilters: PropTypes.func.isRequired,
};

export default RequirementsFilter;
